import { useQueries, useQuery } from "@tanstack/react-query";

import {
  gql,
  GraphQLAPI,
  GraphQLResult,
  queries,
  GraphQLQuery,
} from "@examspark/api";
import { collectAllPages, sortBy } from "@examspark/common";
import {
  AuthorizationContextInput,
  Course,
  QuerySearchArgs,
  SearchableResourceConnectionNew,
  Unit,
  SearchableResourceFilterInput,
  SearchableResourceConnection,
  SearchableResourceSortInput,
  SearchableResourceSortableFields,
  SearchableSortDirection,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";
import { useQueriesGetUnits } from "../operations/unit";

export const useQuerySearchCourses = (params?: {
  workspaceId?: string;
  bankId?: string;
  subject?: string;
  grade?: string;
  sort?: SearchableResourceSortInput[];
}) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["courses", params],
    queryFn: async () => {
      const filter: SearchableResourceFilterInput = {
        sk: {
          eq: "course#v0",
        },
      };

      if (params?.workspaceId != null) {
        filter.workspaceId = { eq: params.workspaceId };
      }
      if (params?.bankId != null) {
        filter.bankId = { eq: params.bankId };
      }
      if (params?.subject != null) {
        filter.subject = { eq: params.subject };
      }
      if (params?.grade != null) {
        filter.grade = { eq: params.grade };
      }

      const pages = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.search.search({
            fields:
              "__typename id sk author name maturity sequence bankId updatedAt numberLabel grade subject language tags { key value } resourcesIds { id } coverImage { key bucket region }",
            label: "SearchCourses",
          }),
          variables: {
            sort: params?.sort ?? [
              {
                field: "updatedAt",
                direction: "desc",
              },
            ],
            filter,
            authorizationContext,
            nextToken,
          },
        })) as GraphQLResult<{ search: SearchableResourceConnection }>;

        return result.data?.search;
      });

      return pages.flatMap((p) => (p.items ?? []) as Course[]);
    },
  });
};

export const useQueryGetCourseWithUnits = (courseId?: string) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["course", courseId, "with-units"],
    enabled: !!courseId,
    queryFn: async () => {
      if (!courseId) return;
      const result = await gql.operations.course.getCourseWithUnits(
        courseId,
        authorizationContext,
      );

      console.log("result", result);
      return result;
    },
  });
};

const getCourseUnits = (
  course: Course | undefined,
  authorizationContext: AuthorizationContextInput,
) => {
  return {
    queryKey: ["course-units", course?.id, course?.unitIds],
    enabled: !!course,
    queryFn: async () => {
      const result = (await GraphQLAPI.query({
        query: queries.search.search({
          fields:
            "__typename id sk name maturity bankId updatedAt numberLabel tags { key value } resourcesIds { id }",
          label: "SearchCourseUnits",
        }),
        variables: {
          // courses can only have 100 units
          limit: 100,
          sort: [
            {
              field: "updatedAt",
              direction: "desc",
            },
          ],
          filter: {
            sk: {
              eq: "unit#v0",
            },
            referencesIds: {
              eq: course?.id,
            },
          } as SearchableResourceFilterInput,
          authorizationContext,
        },
      })) as GraphQLResult<{ search: { items: Unit[] } }>;

      if (result?.data?.search?.items == null) return [];
      return result.data.search.items
        .map((item) => {
          item.__typename = "Unit";
          return item;
        })
        .sort(
          sortBy((unit) => course?.unitIds?.findIndex((u) => u.id === unit.id)),
        );
    },
  };
};

export const useQueryGetCourseUnits = (courseId?: string | null) => {
  const authorizationContext = useAuthorizationContext();
  const { data: course } = useQueryGetCourse(courseId);

  return useQuery(getCourseUnits(course, authorizationContext));
};

export const useQueriesGetUnitsByCourses = (courses: Course[]) => {
  const authorizationContext = useAuthorizationContext();

  return useQueries({
    queries: courses.map((course) =>
      getCourseUnits(course, authorizationContext),
    ),
  });
};
// This query is useful if you need a only-licensed course units but still
// need fields that may not be in ElasticSearch (like duration)
export const useQueryGetCourseUnitsDDB = (
  courseId: string | undefined | null,
) => {
  // this filter to units in your license
  const units = useQueryGetCourseUnits(courseId);

  return useQueriesGetUnits(
    units.data?.map((unit) => ({ id: unit.id, sk: unit.sk })) ?? [],
  );
};

export const getCourse = (
  courseId: string | undefined | null,
  authorizationContext: AuthorizationContextInput,
) => {
  return {
    queryKey: ["course", courseId],
    enabled: !!courseId,
    useErrorBoundary: true,
    queryFn: async () => {
      const result = await GraphQLAPI.query<
        GraphQLQuery<{ getCourse: Course }>
      >({
        query: queries.course.getCourse,
        variables: {
          input: {
            id: courseId,
            sk: "course#v0",
          },
          authorizationContext,
        },
      });

      return result.data?.getCourse;
    },
  };
};

export const useQueryGetCourse = (courseId: string | undefined | null) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery(getCourse(courseId, authorizationContext));
};

export const useQueriesGetCourses = (courseIds: string[]) => {
  const authorizationContext = useAuthorizationContext();

  return useQueries({
    queries: courseIds.map((courseId) =>
      getCourse(courseId, authorizationContext),
    ),
  });
};

export const useQueryGetCourseVerification = (courseId?: string) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    enabled: !!courseId,
    queryKey: ["course", courseId, "verification"],
    queryFn: async () => {
      if (courseId == null) return undefined;

      const result = await gql.operations.course.getCourseVerification(
        courseId,
        authorizationContext,
      );
      return result.data?.getCourse;
    },
  });
};

export const useQueryGetCourseViews = (courseId: string | undefined) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    queryKey: ["course-views", courseId],
    keepPreviousData: true,
    enabled: !!courseId,
    queryFn: async () => {
      const variables: QuerySearchArgs = {
        authorizationContext,
        filter: {
          id: {
            eq: courseId,
          },
          or: [
            {
              sk: {
                eq: "course-verification#v0",
              },
            },
            {
              sk: {
                eq: "course-plan#v0",
              },
            },
          ],
        },
      };

      const result = (await GraphQLAPI.query<{
        search: SearchableResourceConnectionNew;
      }>({
        query: queries.search.search({
          label: "SearchCourseDocuments",
          fields: `
            id
            sk
            name
            type
          `,
        }),
        variables,
      })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

      return result.data?.search.items ?? [];
    },
  });
};

export const useQueryGetCoursesBySubject = (subject: string | undefined) => {
  return useQuerySearchCourses({
    subject,
    sort: [
      {
        field: SearchableResourceSortableFields.Sequence,
        direction: SearchableSortDirection.Asc,
      },
      {
        field: SearchableResourceSortableFields.Name,
        direction: SearchableSortDirection.Asc,
      },
    ],
  });
};
